import * as React from "react";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import { Button, Paper, Stack, Typography } from "@mui/material";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { absoluteJoinUrl } from "../utils/joinPath";

type Props = {
  color:
    | "error"
    | "inherit"
    | "primary"
    | "info"
    | "secondary"
    | "success"
    | "warning"
    | undefined;
  headerText: string | undefined;
  subText: string | undefined;
  joinedPlayers?: string[];
  /** When waiting in lobby, show a copyable invite link (needs room id + namespace). */
  inviteNamespace?: string | null;
  inviteRoomId?: string | null;
};

export const Spinner = ({
  color,
  headerText,
  subText,
  joinedPlayers,
  inviteNamespace,
  inviteRoomId,
}: Props) => {
  const [copyFeedback, setCopyFeedback] = React.useState(false);

  const inviteUrl =
    inviteNamespace && inviteRoomId ? absoluteJoinUrl(inviteNamespace, inviteRoomId) : "";

  const copyInviteLink = async () => {
    if (!inviteUrl) return;
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopyFeedback(true);
      setTimeout(() => setCopyFeedback(false), 2000);
    } catch {
      /* ignore */
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        px: 2,
      }}
    >
      <Paper
        elevation={24}
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          maxWidth: 480,
        }}
      >
        <Stack
          spacing={2}
          sx={{ width: "100%", padding: { xs: 4, sm: 8 }, alignItems: "center" }}
        >
          {headerText ? (
            <Typography variant="h4" sx={{ textAlign: "center" }}>
              {headerText}
            </Typography>
          ) : null}
          {subText ? (
            <Typography variant="h6" sx={{ textAlign: "center", opacity: 0.7 }}>
              {subText}
            </Typography>
          ) : null}
          {inviteUrl ? (
            <Stack spacing={1} sx={{ width: "100%", alignItems: "stretch" }}>
              <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-all", textAlign: "center" }}>
                {inviteUrl}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<ContentCopyIcon />}
                onClick={copyInviteLink}
                sx={{ alignSelf: "center" }}
              >
                {copyFeedback ? "Copied!" : "Copy invite link"}
              </Button>
            </Stack>
          ) : null}
          <CircularProgress color={color} />
          {joinedPlayers && joinedPlayers.length > 0 && (
            <Box sx={{ width: "100%", mt: 1 }}>
              <Typography
                variant="caption"
                sx={{ opacity: 0.6, display: "block", textAlign: "center", mb: 1 }}
              >
                Players in room ({joinedPlayers.length})
              </Typography>
              <Stack spacing={0.5}>
                {joinedPlayers.map((name, i) => {
                  const isBot = /^Blind /i.test(name);
                  return (
                    <Box
                      key={i}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        backgroundColor: "rgba(0,0,0,0.04)",
                        borderRadius: 1,
                        px: 1.5,
                        py: 0.5,
                        opacity: isBot ? 0.6 : 1,
                      }}
                    >
                      {isBot ? (
                        <SmartToyIcon sx={{ fontSize: 12, color: "rgba(0,0,0,0.4)", flexShrink: 0 }} />
                      ) : (
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            backgroundColor: "#4caf50",
                            flexShrink: 0,
                          }}
                        />
                      )}
                      <Typography variant="body2" sx={{ fontStyle: isBot ? "italic" : "normal" }}>
                        {name}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          )}
        </Stack>
      </Paper>
    </Box>
  );
};
